import { cancelBookingInitAction } from "../../../Actions/LogicActions/CancelBooking/CancelBookingInitAction";
import { getPassengerDetailsAction } from "../../../Actions/LogicActions/Checkout/PassengerDetails/GetPassengerDetailsAction";
import { getUserDetailsAction } from "../../../Actions/LogicActions/MyAccount/GetUserDetailsAction";
import { upcomingPastBookingDetailsAction } from "../../../Actions/UIActions/Booking/UpcomingPastBookingDetailsAction";
import { confirmCancelClickAction } from "../../../Actions/UIActions/CancelBooking/ConfirmCancelClickAction";
import { confirmPopupAction } from "../../../Actions/UIActions/CancelBooking/ConfirmPopupAction";
import { mainInboundCheckAction } from "../../../Actions/UIActions/CancelBooking/MainInboundCheckAction";
import { mainOutBoundCheckAction } from "../../../Actions/UIActions/CancelBooking/MainOutBoundCheckAction";
import { passengerOutboundCheckAction } from "../../../Actions/UIActions/CancelBooking/PassengerOutboundCheckAction";
import { ExecutionStatus } from "../../../core/enum/ExecutionStatus";
import { _eventBus } from "../../../core/events/EventBus";
import { StepDefinition } from "../../../core/flows/StepDefinition";
import { StepFlow } from "../../../core/flows/StepFlow";
import { isActionRunSuccessfully } from "../../../Lib/Utils/validationUtil";
import { bookingTree } from "../../../Orchestrator/ConstantTree/BookingPage/BookingTree";
import { cancelBookingTree } from "../../../Orchestrator/ConstantTree/CancelBookingPage/CancelBookingTree";
import { getRecoilStateValue, updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { TripTypeEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import { CancelBookingContext } from "../Context/CancelBookingContext";

export class CancelBookingInitStep extends StepFlow {
    get stepName() { return "CancelBookingInit_Step"; }

    async onEventSetup(): Promise<void> {

        this.registerEventSubscription(
            _eventBus.on({
                eventName: cancelBookingTree.CancelBooking.Init,
                handler: async (data: any) => {
                    cancelBookingInitAction.setParams(data.data!);
                    await cancelBookingInitAction.execute();
                    await getPassengerDetailsAction.execute();
                }
            })
        )

        this.registerEventSubscription(
            _eventBus.on({
                eventName: cancelBookingTree.CancelBooking.PassengerOutboundCheck,
                handler: async (data: any) => {
                    passengerOutboundCheckAction.setParams(data.data!);
                    await passengerOutboundCheckAction.execute();
                }
            })
        )

        this.registerEventSubscription(
            _eventBus.on({
                eventName: cancelBookingTree.CancelBooking.MainOutboundCheck,
                handler: async (data: any) => {
                    mainOutBoundCheckAction.setParams(data.data!);
                    await mainOutBoundCheckAction.execute();
                }
            })
        )

        this.registerEventSubscription(
            _eventBus.on({
                eventName: cancelBookingTree.CancelBooking.MainInboundCheck,
                handler: async (data: any) => {
                    mainInboundCheckAction.setParams(data.data!);
                    await mainInboundCheckAction.execute();
                }
            })
        )

        this.registerEventSubscription(
            _eventBus.on({
                eventName: cancelBookingTree.CancelBooking.ConfirmPopupFlag,
                handler: async (data: any) => {
                    confirmPopupAction.setParams(data.data!);
                    await confirmPopupAction.execute();
                }
            })
        )

        this.registerEventSubscription(
            _eventBus.on({
                eventName: cancelBookingTree.CancelBooking.ConfirmCancelClick,
                handler: async (data: any) => {
                    let context = this.getCtx<CancelBookingContext>("cancelBookingContext")!;
                    context.booking = data.data.booking;
                    confirmCancelClickAction.setParams(data.data!);
                    await confirmCancelClickAction.execute();
                    
                    /**
                     * BOTH conditions. `isActionRunSuccessfully` only asks
                     * BaseAction's executionStatus, which `_runWithCatch` sets to
                     * SUCCESS for ANY non-null return — and this action returns
                     * its result object on the failure path too. So a rejected
                     * release/refund passed this gate and routed to a details
                     * page rendering a cancelled leg for a booking still live on
                     * the backend. The action's own `status` is the answer to
                     * "did the cancellation happen".
                     */
                    if (isActionRunSuccessfully(confirmCancelClickAction)
                        && confirmCancelClickAction.result?.status === "success") {
                        //Reset Upgrade Recoil States
                        updateRecoilState("upgradeOutboundOffersAtom", null);
                        updateRecoilState("upgradeInboundOffersAtom", null);

                        // Add Logic to show Cancelled Booking only after cancel success. - Build split tripData to show only the cancelled leg(s) on details page
                        const outboundTrip = data.data.booking?.trips?.find((t: any) => t.direction === TripTypeEnum.OUT_BOUND);
                        const inboundTrip = data.data.booking?.trips?.find((t: any) => t.direction === TripTypeEnum.IN_BOUND);
                        
                        // Helper function to get passenger IDs by admission status
                        const getPassengerIdsByStatus = (offer: any, status: string) => {
                            return (
                                offer?.admissions
                                    ?.filter((a: any) => a.status === status)
                                    ?.flatMap((a: any) => a.passengerIds) || []
                            );
                        };

                        // Helper function to get all passenger IDs from a trip
                        const getAllPassengerIdsFromTrip = (trip: any) => {
                            if (!trip) return [];
                           
                            const tripOffer = data.data.booking?.bookedOffers?.find((offer: any) =>
                                offer.tripCoverage?.coveredTripId === trip.id
                            );
                           
                            if (!tripOffer) return [];
                           
                            return tripOffer.admissions?.flatMap((admission: any) => admission.passengerIds) || [];
                        };

                        // Helper function to check if all passengers from a trip are being cancelled (including already refunded)
                        const areAllPassengersBeingCancelled = (trip: any, cancelledPassengerIds: string[]) => {
                            if (!trip) return false;
                           
                            const tripOffer = data.data.booking?.bookedOffers?.find((offer: any) =>
                                offer.tripCoverage?.coveredTripId === trip.id
                            );
                           
                            if (!tripOffer) return false;
                           
                            const allTripPassengerIds = tripOffer.admissions?.flatMap((admission: any) => admission.passengerIds) || [];
                            const alreadyRefundedPassengerIds = getPassengerIdsByStatus(tripOffer, "REFUNDED");
                           
                            // Combine current cancellation selection with already refunded passengers
                            const allCancelledPassengerIds = Array.from(new Set([
                                ...cancelledPassengerIds,
                                ...alreadyRefundedPassengerIds
                            ]));
                           
                            return allTripPassengerIds.length > 0 &&
                                   allTripPassengerIds.every((passengerId: string) =>
                                       allCancelledPassengerIds.includes(passengerId)
                                   );
                        };

                        // Helper function to check if any passengers from a trip are being cancelled (including already refunded)
                        const hasAnyPassengersBeingCancelled = (trip: any, cancelledPassengerIds: string[]) => {
                            if (!trip) return false;
                           
                            const tripOffer = data.data.booking?.bookedOffers?.find((offer: any) =>
                                offer.tripCoverage?.coveredTripId === trip.id
                            );
                           
                            if (!tripOffer) return false;
                           
                            const allTripPassengerIds = tripOffer.admissions?.flatMap((admission: any) => admission.passengerIds) || [];
                            const alreadyRefundedPassengerIds = getPassengerIdsByStatus(tripOffer, "REFUNDED");
                           
                            // Combine current cancellation selection with already refunded passengers
                            const allCancelledPassengerIds = Array.from(new Set([
                                ...cancelledPassengerIds,
                                ...alreadyRefundedPassengerIds
                            ]));
                           
                            return allTripPassengerIds.some((passengerId: string) =>
                                allCancelledPassengerIds.includes(passengerId)
                            );
                        };
                       
                        const cancelledTripIds: string[] = [];
                        const allTripIds: string[] = [];
                       
                        // Check outbound trip
                        if (outboundTrip) {
                            allTripIds.push(outboundTrip.id);
                            if (hasAnyPassengersBeingCancelled(outboundTrip, data.data.selectedPassengerForCancel || [])) {
                                cancelledTripIds.push(outboundTrip.id);
                            }
                        }
                       
                        // Check inbound trip
                        if (inboundTrip) {
                            allTripIds.push(inboundTrip.id);
                            if (hasAnyPassengersBeingCancelled(inboundTrip, data.data.selectedPassengerForCancelInBound || [])) {
                                cancelledTripIds.push(inboundTrip.id);
                            }
                        }

                        // Determine which trips to show based on cancellation pattern
                        let tripIdsToShow: string[] = [];
                        
                        // Check if this is a round trip (both outbound and inbound exist)
                        const isRoundTrip = outboundTrip && inboundTrip;
                        
                        if (isRoundTrip) {
                            const allOutboundCancelled = areAllPassengersBeingCancelled(outboundTrip, data.data.selectedPassengerForCancel || []);
                            const allInboundCancelled = areAllPassengersBeingCancelled(inboundTrip, data.data.selectedPassengerForCancelInBound || []);
                            
                            const hasOutboundCancelled = hasAnyPassengersBeingCancelled(outboundTrip, data.data.selectedPassengerForCancel || []);
                            const hasInboundCancelled = hasAnyPassengersBeingCancelled(inboundTrip, data.data.selectedPassengerForCancelInBound || []);
                            
                            if (allOutboundCancelled && !allInboundCancelled) {
                                tripIdsToShow = [outboundTrip.id];
                            } else if (allInboundCancelled && !allOutboundCancelled) {
                                tripIdsToShow = [inboundTrip.id];
                            } else if (allOutboundCancelled && allInboundCancelled) {
                                tripIdsToShow = allTripIds;
                            } else if (hasOutboundCancelled && hasInboundCancelled) {
                                tripIdsToShow = allTripIds;
                            } else {
                                tripIdsToShow = allTripIds;
                            }
                        } else {
                            tripIdsToShow = [];
                        }
 
                        // Get all refunded passenger IDs from both trips
                        const getAllRefundedPassengerIds = () => {
                            const refundedPassengerIds: string[] = [];
                            
                            // Add outbound cancelled passengers (current selection + already refunded)
                            if (outboundTrip) {
                                const outboundOffer = data.data.booking?.bookedOffers?.find((offer: any) =>
                                    offer.tripCoverage?.coveredTripId === outboundTrip.id
                                );
                                if (outboundOffer) {
                                    const outboundRefundedIds = getPassengerIdsByStatus(outboundOffer, "REFUNDED");
                                    refundedPassengerIds.push(...outboundRefundedIds);
                                }
                            }
                            if (Array.isArray(data.data.selectedPassengerForCancel) && data.data.selectedPassengerForCancel.length > 0) {
                                refundedPassengerIds.push(...data.data.selectedPassengerForCancel);
                            }
                            
                            // Add inbound cancelled passengers (current selection + already refunded)
                            if (inboundTrip) {
                                const inboundOffer = data.data.booking?.bookedOffers?.find((offer: any) =>
                                    offer.tripCoverage?.coveredTripId === inboundTrip.id
                                );
                                if (inboundOffer) {
                                    const inboundRefundedIds = getPassengerIdsByStatus(inboundOffer, "REFUNDED");
                                    refundedPassengerIds.push(...inboundRefundedIds);
                                }
                            }
                            if (Array.isArray(data.data.selectedPassengerForCancelInBound) && data.data.selectedPassengerForCancelInBound.length > 0) {
                                refundedPassengerIds.push(...data.data.selectedPassengerForCancelInBound);
                            }
                            
                            return Array.from(new Set(refundedPassengerIds)); // Remove duplicates
                        };
 
                        const splitTripData = {
                            // tripIds is used by GetBookingDetailDataAction to keep only these trips
                            tripIds: tripIdsToShow,
                            // passengerData is only checked for existence to enable split branch
                            passengerData: {
                                outbound: data.data.selectedPassengerForCancel || [],
                                inbound: data.data.selectedPassengerForCancelInBound || []
                            },
                            // Keep original passengers in case UI needs them
                            passengers: data.data.booking?.passengers || [],
                            // Add all refunded passenger IDs for proper tracking
                            refundedPassengerIds: getAllRefundedPassengerIds()
                        };
 
                        upcomingPastBookingDetailsAction.setParams({
                            isPast: false,
                            bookingId: data.data.booking.id,
                            tripStatus: "REFUNDED",
                            repeatBooking: undefined,
                            direction: undefined,
                            tripData: splitTripData //data.data.booking
                        })
                        await upcomingPastBookingDetailsAction.execute();
                        if (upcomingPastBookingDetailsAction.status.executionStatus == ExecutionStatus.SUCCESS) {
                            _eventBus.emit({
                                eventName: "FLOW_CLOSE",
                                data: "AfterSalesBookingFlow",
                            });
 
                            _eventBus.emit({
                                eventName: "ROUTE_CHANGE",
                                data: `/bookings/details/${upcomingPastBookingDetailsAction.result}`,
                            });
                        }
                    }
                }
            })
        )

        this.registerEventSubscription(
            _eventBus.on({
                eventName: cancelBookingTree.CancelBooking.BackToBookingClick,
                handler: async () => {
                    const previousRoute = getRecoilStateValue<string>("cancelBookingPreviousRouteAtom");
                    const bookingId = getRecoilStateValue<string>("selectedBookingIdForCancelAtom");
                    
                    const redirectRoute = previousRoute || (bookingId ? `/bookings/details/${bookingId}` : "/bookings");
                    _eventBus.emit({
                        eventName: "ROUTE_CHANGE",
                        data: redirectRoute,
                    });
                }
            })
        )
    }

    async onExecute(): Promise<void> {
        this.setCtx("cancelBookingContext", CancelBookingContext);
        console.log("Cancel Booking Initialize.....")
    }

}

export const CancelBookingInitStepDeclaration = new StepDefinition({
    stepName: "CancelBookingInit_Step",
    stepClass: CancelBookingInitStep
});